/**
 * @module usePilot
 */
import { useCallback } from 'react'
import {
  useGetPilotsMutation,
  useGetPilotMutation,
  useGetPilotByNumberMutation,
  useCreatePilotMutation,
  useUpdatePilotMutation,
  useDeletePilotMutation,
  useUpdatePilotStatusMutation,
  useUpdateLicenseMutation,
  useUpdateMedicalMutation,
  useUpdateTrainingMutation,
  useUpdateFlightExperienceMutation,
  useUpdateEmergencyContactMutation,
  useUpdateAddressMutation,
  useAddTypeRatingMutation,
  useRemoveTypeRatingMutation,
  useAddFlightHoursMutation,
  useUpdateInstrumentRatingMutation,
  useTerminateMutation,
  useReactivateMutation,
  useGetPilotsByAirlineMutation,
  useGetPilotsByAircraftMutation,
  useGetPilotsNeedingRecurrentTrainingMutation,
  useGetPilotsWithExpiringMedicalMutation,
  useGetPilotsWithExpiringLicenseMutation,
  useGetPilotsWithExpiringSimulatorCheckMutation,
} from '../services/pilot.service'

export const usePilot = () => {
  // Basic CRUD operations
  const [executeGetPilots, { isLoading: isGetPilotsLoading }] = useGetPilotsMutation()
  const [executeGetPilot, { isLoading: isGetPilotLoading }] = useGetPilotMutation()
  const [executeGetPilotByNumber, { isLoading: isGetPilotByNumberLoading }] =
    useGetPilotByNumberMutation()
  const [executeCreatePilot, { isLoading: isCreatePilotLoading }] = useCreatePilotMutation()
  const [executeUpdatePilot, { isLoading: isUpdatePilotLoading }] = useUpdatePilotMutation()
  const [executeDeletePilot, { isLoading: isDeletePilotLoading }] = useDeletePilotMutation()

  // Status and profile updates
  const [executeUpdatePilotStatus, { isLoading: isUpdatePilotStatusLoading }] =
    useUpdatePilotStatusMutation()
  const [executeUpdateLicense, { isLoading: isUpdateLicenseLoading }] = useUpdateLicenseMutation()
  const [executeUpdateMedical, { isLoading: isUpdateMedicalLoading }] = useUpdateMedicalMutation()
  const [executeUpdateTraining, { isLoading: isUpdateTrainingLoading }] =
    useUpdateTrainingMutation()
  const [executeUpdateFlightExperience, { isLoading: isUpdateFlightExperienceLoading }] =
    useUpdateFlightExperienceMutation()
  const [executeUpdateEmergencyContact, { isLoading: isUpdateEmergencyContactLoading }] =
    useUpdateEmergencyContactMutation()
  const [executeUpdateAddress, { isLoading: isUpdateAddressLoading }] = useUpdateAddressMutation()

  // Type rating operations
  const [executeAddTypeRating, { isLoading: isAddTypeRatingLoading }] = useAddTypeRatingMutation()
  const [executeRemoveTypeRating, { isLoading: isRemoveTypeRatingLoading }] =
    useRemoveTypeRatingMutation()

  // Flight hours and ratings
  const [executeAddFlightHours, { isLoading: isAddFlightHoursLoading }] =
    useAddFlightHoursMutation()
  const [executeUpdateInstrumentRating, { isLoading: isUpdateInstrumentRatingLoading }] =
    useUpdateInstrumentRatingMutation()

  // Employment status
  const [executeTerminate, { isLoading: isTerminateLoading }] = useTerminateMutation()
  const [executeReactivate, { isLoading: isReactivateLoading }] = useReactivateMutation()

  // Airline and aircraft operations
  const [executeGetPilotsByAirline, { isLoading: isGetPilotsByAirlineLoading }] =
    useGetPilotsByAirlineMutation()
  const [executeGetPilotsByAircraft, { isLoading: isGetPilotsByAircraftLoading }] =
    useGetPilotsByAircraftMutation()

  // Compliance and monitoring
  const [
    executeGetPilotsNeedingRecurrentTraining,
    { isLoading: isGetPilotsNeedingRecurrentTrainingLoading },
  ] = useGetPilotsNeedingRecurrentTrainingMutation()
  const [
    executeGetPilotsWithExpiringMedical,
    { isLoading: isGetPilotsWithExpiringMedicalLoading },
  ] = useGetPilotsWithExpiringMedicalMutation()
  const [
    executeGetPilotsWithExpiringLicense,
    { isLoading: isGetPilotsWithExpiringLicenseLoading },
  ] = useGetPilotsWithExpiringLicenseMutation()
  const [
    executeGetPilotsWithExpiringSimulatorCheck,
    { isLoading: isGetPilotsWithExpiringSimulatorCheckLoading },
  ] = useGetPilotsWithExpiringSimulatorCheckMutation()

  // Basic CRUD async functions
  const getPilotsAsync = useCallback(async () => {
    if (isGetPilotsLoading) {
      return
    }
    try {
      const response = await executeGetPilots().unwrap()
      return response.data.pilots
    } catch (error) {
      console.error('#getPilotsAsync: Error fetching pilots', error)
      throw error
    }
  }, [executeGetPilots, isGetPilotsLoading])

  const getPilotAsync = useCallback(
    async (pilotId) => {
      if (isGetPilotLoading) {
        return
      }
      try {
        const response = await executeGetPilot(pilotId).unwrap()
        return response.data.pilot
      } catch (error) {
        console.error('#getPilotAsync: Error fetching pilot', error)
        throw error
      }
    },
    [executeGetPilot, isGetPilotLoading],
  )

  const getPilotByNumberAsync = useCallback(
    async (number) => {
      if (isGetPilotByNumberLoading) {
        return
      }
      try {
        const response = await executeGetPilotByNumber(number).unwrap()
        return response.data.pilot
      } catch (error) {
        console.error('#getPilotByNumberAsync: Error fetching pilot by number', error)
        throw error
      }
    },
    [executeGetPilotByNumber, isGetPilotByNumberLoading],
  )

  const createPilotAsync = useCallback(
    async (pilot) => {
      if (isCreatePilotLoading) {
        return
      }
      try {
        const response = await executeCreatePilot(pilot).unwrap()
        return response.data.pilot
      } catch (error) {
        console.error('#createPilotAsync: Error creating pilot', error)
        throw error
      }
    },
    [executeCreatePilot, isCreatePilotLoading],
  )

  const updatePilotAsync = useCallback(
    async (pilotId, updates) => {
      if (isUpdatePilotLoading) {
        return
      }
      try {
        const response = await executeUpdatePilot({ pilotId, ...updates }).unwrap()
        return response.data.pilot
      } catch (error) {
        console.error('#updatePilotAsync: Error updating pilot', error)
        throw error
      }
    },
    [executeUpdatePilot, isUpdatePilotLoading],
  )

  const deletePilotAsync = useCallback(
    async (pilotId) => {
      if (isDeletePilotLoading) {
        return
      }
      try {
        const response = await executeDeletePilot(pilotId).unwrap()
        return response.data
      } catch (error) {
        console.error('#deletePilotAsync: Error deleting pilot', error)
        throw error
      }
    },
    [executeDeletePilot, isDeletePilotLoading],
  )

  // Status and profile async functions
  const updatePilotStatusAsync = useCallback(
    async (pilotId, status) => {
      if (isUpdatePilotStatusLoading) {
        return
      }
      try {
        const response = await executeUpdatePilotStatus({ pilotId, status }).unwrap()
        return response.data.pilot
      } catch (error) {
        console.error('#updatePilotStatusAsync: Error updating pilot status', error)
        throw error
      }
    },
    [executeUpdatePilotStatus, isUpdatePilotStatusLoading],
  )

  const updateLicenseAsync = useCallback(
    async (pilotId, license) => {
      if (isUpdateLicenseLoading) {
        return
      }
      try {
        const response = await executeUpdateLicense({ pilotId, license }).unwrap()
        return response.data.pilot
      } catch (error) {
        console.error('#updateLicenseAsync: Error updating license', error)
        throw error
      }
    },
    [executeUpdateLicense, isUpdateLicenseLoading],
  )

  const updateMedicalAsync = useCallback(
    async (pilotId, medical) => {
      if (isUpdateMedicalLoading) {
        return
      }
      try {
        const response = await executeUpdateMedical({ pilotId, medical }).unwrap()
        return response.data.pilot
      } catch (error) {
        console.error('#updateMedicalAsync: Error updating medical', error)
        throw error
      }
    },
    [executeUpdateMedical, isUpdateMedicalLoading],
  )

  const updateTrainingAsync = useCallback(
    async (pilotId, training) => {
      if (isUpdateTrainingLoading) {
        return
      }
      try {
        const response = await executeUpdateTraining({ pilotId, training }).unwrap()
        return response.data.pilot
      } catch (error) {
        console.error('#updateTrainingAsync: Error updating training', error)
        throw error
      }
    },
    [executeUpdateTraining, isUpdateTrainingLoading],
  )

  const updateFlightExperienceAsync = useCallback(
    async (pilotId, flightExperience) => {
      if (isUpdateFlightExperienceLoading) {
        return
      }
      try {
        const response = await executeUpdateFlightExperience({ pilotId, flightExperience }).unwrap()
        return response.data.pilot
      } catch (error) {
        console.error('#updateFlightExperienceAsync: Error updating flight experience', error)
        throw error
      }
    },
    [executeUpdateFlightExperience, isUpdateFlightExperienceLoading],
  )

  const updateEmergencyContactAsync = useCallback(
    async (pilotId, emergencyContact) => {
      if (isUpdateEmergencyContactLoading) {
        return
      }
      try {
        const response = await executeUpdateEmergencyContact({ pilotId, emergencyContact }).unwrap()
        return response.data.pilot
      } catch (error) {
        console.error('#updateEmergencyContactAsync: Error updating emergency contact', error)
        throw error
      }
    },
    [executeUpdateEmergencyContact, isUpdateEmergencyContactLoading],
  )

  const updateAddressAsync = useCallback(
    async (pilotId, address) => {
      if (isUpdateAddressLoading) {
        return
      }
      try {
        const response = await executeUpdateAddress({ pilotId, address }).unwrap()
        return response.data.pilot
      } catch (error) {
        console.error('#updateAddressAsync: Error updating address', error)
        throw error
      }
    },
    [executeUpdateAddress, isUpdateAddressLoading],
  )

  // Type rating async functions
  const addTypeRatingAsync = useCallback(
    async (pilotId, typeRating) => {
      if (isAddTypeRatingLoading) {
        return
      }
      try {
        const response = await executeAddTypeRating({ pilotId, typeRating }).unwrap()
        return response.data.pilot
      } catch (error) {
        console.error('#addTypeRatingAsync: Error adding type rating', error)
        throw error
      }
    },
    [executeAddTypeRating, isAddTypeRatingLoading],
  )

  const removeTypeRatingAsync = useCallback(
    async (pilotId, rating) => {
      if (isRemoveTypeRatingLoading) {
        return
      }
      try {
        const response = await executeRemoveTypeRating({ pilotId, rating }).unwrap()
        return response.data.pilot
      } catch (error) {
        console.error('#removeTypeRatingAsync: Error removing type rating', error)
        throw error
      }
    },
    [executeRemoveTypeRating, isRemoveTypeRatingLoading],
  )

  // Flight hours and ratings async functions
  const addFlightHoursAsync = useCallback(
    async (pilotId, flightHours) => {
      if (isAddFlightHoursLoading) {
        return
      }
      try {
        const response = await executeAddFlightHours({ pilotId, flightHours }).unwrap()
        return response.data.pilot
      } catch (error) {
        console.error('#addFlightHoursAsync: Error adding flight hours', error)
        throw error
      }
    },
    [executeAddFlightHours, isAddFlightHoursLoading],
  )

  const updateInstrumentRatingAsync = useCallback(
    async (pilotId, instrumentRating) => {
      if (isUpdateInstrumentRatingLoading) {
        return
      }
      try {
        const response = await executeUpdateInstrumentRating({ pilotId, instrumentRating }).unwrap()
        return response.data.pilot
      } catch (error) {
        console.error('#updateInstrumentRatingAsync: Error updating instrument rating', error)
        throw error
      }
    },
    [executeUpdateInstrumentRating, isUpdateInstrumentRatingLoading],
  )

  // Employment status async functions
  const terminateAsync = useCallback(
    async (pilotId) => {
      if (isTerminateLoading) {
        return
      }
      try {
        const response = await executeTerminate(pilotId).unwrap()
        return response.data.pilot
      } catch (error) {
        console.error('#terminateAsync: Error terminating pilot', error)
        throw error
      }
    },
    [executeTerminate, isTerminateLoading],
  )

  const reactivateAsync = useCallback(
    async (pilotId) => {
      if (isReactivateLoading) {
        return
      }
      try {
        const response = await executeReactivate(pilotId).unwrap()
        return response.data.pilot
      } catch (error) {
        console.error('#reactivateAsync: Error reactivating pilot', error)
        throw error
      }
    },
    [executeReactivate, isReactivateLoading],
  )

  // Airline and aircraft async functions
  const getPilotsByAirlineAsync = useCallback(
    async (airlineId) => {
      if (isGetPilotsByAirlineLoading) {
        return
      }
      try {
        const response = await executeGetPilotsByAirline(airlineId).unwrap()
        return response.data.pilots
      } catch (error) {
        console.error('#getPilotsByAirlineAsync: Error fetching pilots by airline', error)
        throw error
      }
    },
    [executeGetPilotsByAirline, isGetPilotsByAirlineLoading],
  )

  const getPilotsByAircraftAsync = useCallback(
    async (aircraftId) => {
      if (isGetPilotsByAircraftLoading) {
        return
      }
      try {
        const response = await executeGetPilotsByAircraft(aircraftId).unwrap()
        return response.data.pilots
      } catch (error) {
        console.error('#getPilotsByAircraftAsync: Error fetching pilots by aircraft', error)
        throw error
      }
    },
    [executeGetPilotsByAircraft, isGetPilotsByAircraftLoading],
  )

  // Compliance and monitoring async functions
  const getPilotsNeedingRecurrentTrainingAsync = useCallback(async () => {
    if (isGetPilotsNeedingRecurrentTrainingLoading) {
      return
    }
    try {
      const response = await executeGetPilotsNeedingRecurrentTraining().unwrap()
      return response.data.pilots
    } catch (error) {
      console.error(
        '#getPilotsNeedingRecurrentTrainingAsync: Error fetching pilots needing recurrent training',
        error,
      )
      throw error
    }
  }, [executeGetPilotsNeedingRecurrentTraining, isGetPilotsNeedingRecurrentTrainingLoading])

  const getPilotsWithExpiringMedicalAsync = useCallback(async () => {
    if (isGetPilotsWithExpiringMedicalLoading) {
      return
    }
    try {
      const response = await executeGetPilotsWithExpiringMedical().unwrap()
      return response.data.pilots
    } catch (error) {
      console.error(
        '#getPilotsWithExpiringMedicalAsync: Error fetching pilots with expiring medical',
        error,
      )
      throw error
    }
  }, [executeGetPilotsWithExpiringMedical, isGetPilotsWithExpiringMedicalLoading])

  const getPilotsWithExpiringLicenseAsync = useCallback(async () => {
    if (isGetPilotsWithExpiringLicenseLoading) {
      return
    }
    try {
      const response = await executeGetPilotsWithExpiringLicense().unwrap()
      return response.data.pilots
    } catch (error) {
      console.error(
        '#getPilotsWithExpiringLicenseAsync: Error fetching pilots with expiring license',
        error,
      )
      throw error
    }
  }, [executeGetPilotsWithExpiringLicense, isGetPilotsWithExpiringLicenseLoading])

  const getPilotsWithExpiringSimulatorCheckAsync = useCallback(async () => {
    if (isGetPilotsWithExpiringSimulatorCheckLoading) {
      return
    }
    try {
      const response = await executeGetPilotsWithExpiringSimulatorCheck().unwrap()
      return response.data.pilots
    } catch (error) {
      console.error(
        '#getPilotsWithExpiringSimulatorCheckAsync: Error fetching pilots with expiring simulator check',
        error,
      )
      throw error
    }
  }, [executeGetPilotsWithExpiringSimulatorCheck, isGetPilotsWithExpiringSimulatorCheckLoading])

  return {
    // Basic CRUD functions
    getPilotsAsync,
    getPilotAsync,
    getPilotByNumberAsync,
    createPilotAsync,
    updatePilotAsync,
    deletePilotAsync,

    // Status and profile functions
    updatePilotStatusAsync,
    updateLicenseAsync,
    updateMedicalAsync,
    updateTrainingAsync,
    updateFlightExperienceAsync,
    updateEmergencyContactAsync,
    updateAddressAsync,

    // Type rating functions
    addTypeRatingAsync,
    removeTypeRatingAsync,

    // Flight hours and ratings functions
    addFlightHoursAsync,
    updateInstrumentRatingAsync,

    // Employment status functions
    terminateAsync,
    reactivateAsync,

    // Airline and aircraft functions
    getPilotsByAirlineAsync,
    getPilotsByAircraftAsync,

    // Compliance and monitoring functions
    getPilotsNeedingRecurrentTrainingAsync,
    getPilotsWithExpiringMedicalAsync,
    getPilotsWithExpiringLicenseAsync,
    getPilotsWithExpiringSimulatorCheckAsync,

    // Loading states
    isGetPilotsLoading,
    isGetPilotLoading,
    isGetPilotByNumberLoading,
    isCreatePilotLoading,
    isUpdatePilotLoading,
    isDeletePilotLoading,
    isUpdatePilotStatusLoading,
    isUpdateLicenseLoading,
    isUpdateMedicalLoading,
    isUpdateTrainingLoading,
    isUpdateFlightExperienceLoading,
    isUpdateEmergencyContactLoading,
    isUpdateAddressLoading,
    isAddTypeRatingLoading,
    isRemoveTypeRatingLoading,
    isAddFlightHoursLoading,
    isUpdateInstrumentRatingLoading,
    isTerminateLoading,
    isReactivateLoading,
    isGetPilotsByAirlineLoading,
    isGetPilotsByAircraftLoading,
    isGetPilotsNeedingRecurrentTrainingLoading,
    isGetPilotsWithExpiringMedicalLoading,
    isGetPilotsWithExpiringLicenseLoading,
    isGetPilotsWithExpiringSimulatorCheckLoading,
  }
}
